<script setup>
import { ref, onMounted, onUnmounted } from 'vue';
import { useLayoutComponents } from '@/composables/layout.js';
import { useIcons } from '@/composables/icons.js';
import { useToast } from '@/composables/use-toast.js';
import axios from '@/plugins/axios';
import { useUserStore } from '@/store/UserStore';

const userStore = useUserStore();

// Composables
const { TextInput, PrimaryButton, ButtonLoading, Toast } = useLayoutComponents();
const { Envelope } = useIcons();
const { addToast } = useToast();

// Reactive state
const email = ref('');
const loading = ref(false);

const forgotPassword = async () => {
  try {
    loading.value = true;
    const response = await axios.post('/api/login/forgot-password', {
      email: email.value,
    });

    if (response.data.statusCode === 200) {
      addToast('A password reset email has been sent to your email address, please check your inbox and click the link to reset your password', 'success');
    } else {
      addToast('Failed to send password reset email', 'error');
    }
  } catch (error) {
    addToast(error.response?.data?.message || 'Failed to send password reset email', 'error');
  } finally {
    loading.value = false;
  }
};

onMounted(() => {
  if (userStore.getEmail) {
    email.value = userStore.getEmail;
  }
});

onUnmounted(() => {
  userStore.setEmail(null);
});
</script>

<template>
  <Navbar :navLight="'nav-light'" />
  <Toast />
  <div class="forgot-password">
    <div class="forgot-password-card bg-echoes-background-black border border-echoes-border-gray">
      <!-- Header -->
      <div class="forgot-password-card__header">
        <img src="/favicon.ico" alt="logo" class="forgot-password-card__header-logo" />
        <h2 class="forgot-password-card__header-title">Forgot Password</h2>
        <span class="forgot-password-card__header-subtitle">
          Forgot your account password
        </span>
      </div>
      
      <!-- Body -->
      <div class="forgot-password-card__body">
        <form @submit.prevent="forgotPassword" class="forgot-password-card__body-form">
          <!-- Email input -->
          <div class="forgot-password-card__body-form-group">
            <TextInput v-model="email" type="email" id="email" placeholder="email address" required>
              <template #icon>
                <Envelope class="w-5 h-5" />
              </template>
            </TextInput>
          </div>

          <!-- reset-password button -->
          <div class="text-center">
            <PrimaryButton class="forgot-password-card__body-form-button" :disabled="loading">
              <ButtonLoading v-if="loading" size="sm" text="Sending reset email..." />
              <template v-else>Send reset email</template>
            </PrimaryButton>
          </div>
        </form>

        <div class="text-center">
          <router-link to="/login" class="text-sm text-white/80 hover:text-echoes-primary font-semibold">
            Back to Login
          </router-link>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="postcss">
.forgot-password {
  @apply flex justify-center items-center h-[100dvh] w-screen px-4;
  background-image: linear-gradient(to bottom, #232323, #000000);
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
}

.forgot-password-card {
  @apply w-full max-w-sm rounded-2xl p-4 shadow-lg;
  backdrop-filter: blur(8px);
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.3), 0 4px 6px rgba(0, 0, 0, 0.2);
  border: 1px solid transparent;
  background-clip: padding-box;
}

.forgot-password-card__header {
  @apply mb-4 flex flex-col items-center justify-center gap-2;
}

.forgot-password-card__header-title {
  @apply text-2xl font-semibold text-white;
}

.forgot-password-card__header-subtitle {
  @apply text-sm text-gray-400;
}

.forgot-password-card__header-logo {
  @apply w-12 h-12;
}

.forgot-password-card__body {
  @apply space-y-4;
}

.forgot-password-card__body-form {
  @apply flex flex-col gap-2;
}

.forgot-password-card__body-form-group {
  @apply flex flex-col gap-2;
}

.forgot-password-card__body-form-button {
  @apply w-full shadow-lg;
}

.forgot-password-card__footer {
  @apply mt-6 flex justify-between;
}

.social-reset-password-buttons {
  @apply hidden justify-center gap-4;
}

.social-button {
  @apply flex items-center justify-center gap-2 w-full p-2 rounded-lg bg-zinc-800 hover:bg-zinc-700 transition-colors shadow-md;
}
</style>